<template>
  <div class="price-rules-search row" v-if="canSearch">
    <div class="col-md-12 card advanced-search">
      <div class="card-header">
        <h4 class="card-title" style="display:inline">
          Price Rules Advanced Search
        </h4>
        <button type="button" v-if="canEdit" @click="addPriceRule" class="btn btn-fill btn-info btn-add-price-rule pull-right">
          Add Price Rule
        </button>
      </div>
      <div class="card-body form-group">
        <div class="row">
          <div class="col-md-3">
            <label>Search</label>
            <el-input type="search"
                   placeholder="Search"
                   name="searchText"
                   clearable
                   v-model="query.searchText"
                   @keyup.enter.native="fetchSearchResults"
                   v-validate="'minFields'"
                   data-vv-as="search"
                   data-vv-name="querySearch" />
            <span class="text-danger" v-show="errors.has('querySearch')">
                {{ mainSearchFieldErrorText }}
            </span>
          </div>
          <div class="col-md-3">
            <label>Department(s)
              <span class="text-danger" v-show="errors.has('querySearch')">
                {{ otherSearchFieldErrorText }}
              </span>
            </label>
            <el-select multiple class="select"
                       size="large"
                       clearable
                       placeholder="Departments"
                       data-vv-as="departments"
                       data-vv-name="'departments"
                       v-model="query.departments"
                       @clear="resetValidator">
              <el-option v-for="option in allDepartments"
                         class="select"
                         :value="option.label"
                         :label="option.label"
                         :key="option.label">
              </el-option>
            </el-select>
          </div>
          <div class="col-md-3">
            <label>Project Type
              <span class="text-danger" v-show="errors.has('querySearch')">
                {{ otherSearchFieldErrorText }}
              </span>
            </label>
            <el-select class="select"
                       size="large"
                       placeholder="Type"
                       clearable
                       v-model="query.projectType">
              <el-option v-for="option in projectTypes"
                         class="select"
                         :value="option.value"
                         :label="option.label"
                         :key="option.label">
              </el-option>
            </el-select>
          </div>
          <div class="col-md-3">
            <p-checkbox style="margin-top: 3px;"
                        name="hourly"
                        v-model="query.hourly">
              Hourly
            </p-checkbox>
            <p-checkbox style="margin-top: 3px;"
                        name="repeatedTask"
                        v-model="query.repeatedTask">
              Repeated Task
            </p-checkbox>
          </div>
        </div>
        <div class="row" style="margin-top: 12px;">
          <div class="col-md-3">
            <label>Task Name
              <span class="text-danger" v-show="errors.has('querySearch')">
                {{ otherSearchFieldErrorText }}
              </span>
            </label>
            <v-select
              v-model="selectedTaskName"
              placeholder="Task Name"
              @input="setTaskName"
              label="label"
              value="value"
              :options="taskNamesForSelect">
            </v-select>
          </div>
          <div class="col-md-3">
            <label>
              Job Name
              <span class="text-danger" v-show="errors.has('querySearch')">
                {{ otherSearchFieldErrorText }}
              </span>
              <el-tooltip class="item" effect="dark" content="If no department(s) are selected above, your default department is used to filter the list of available jobs." placement="top-start">
                <img class="icon-help" src="static/img/icon-help.png"/>
              </el-tooltip>
            </label>
            <v-select
              v-model="selectedJobName"
              placeholder="Job Name"
              @input="setJobName"
              label="label"
              value="value"
              :options="jobNames">
            </v-select>
          </div>
          <div class="col-md-3">
            <label>Project Name
              <span class="text-danger" v-show="errors.has('querySearch')">
                {{ otherSearchFieldErrorText }}
              </span>
            </label>
            <el-input type="text"
                      name="projectName"
                      placeholder="Project Name"
                      clearable
                      v-model="query.projectName"
                      data-vv-name="projectName" />
          </div>
          <div class="col-md-3">
            <p-checkbox style="margin-top: 3px;"
                        name="clientDefault"
                        v-model="query.clientDefault">
              Client Default
            </p-checkbox>
            <p-checkbox style="margin-top: 3px;"
                        name="projectDefault"
                        v-model="query.projectDefault">
              Project Default
            </p-checkbox>
          </div>
        </div>
        <div class="row" style="margin-top: 12px;">
          <div class="col-md-3">
            <label>Community
              <span class="text-danger" v-show="errors.has('querySearch')">
                {{ otherSearchFieldErrorText }}
              </span>
            </label>
            <el-autocomplete
              style="width:100%"
              ref="communityId"
              name="community"
              class="inline-input"
              clearable
              v-model="selectedCommunity.name"
              :fetch-suggestions="findCommunities"
              :trigger-on-focus="false"
              value-key="name"
              placeholder="Community"
              @blur="verifyCommunityAutocompleteSelected"
              @select="setCommunity">
            </el-autocomplete>
            <small class="text-danger" v-show="showInvalidAutocompleteWarning('communityId')">
              {{ fieldNotValidError('Community') }}
            </small>
          </div>
          <div class="col-md-3">
            <label>Client
              <span class="text-danger" v-show="errors.has('querySearch')">
                {{ otherSearchFieldErrorText }}
              </span>
            </label>
            <el-autocomplete
              style="width:100%"
              class="inline-input"
              clearable
              name="client"
              ref="clientId"
              v-model="selectedClient.name"
              :fetch-suggestions="findClients"
              :trigger-on-focus="false"
              value-key="name"
              placeholder="Client"
              @blur="verifyClientAutocompleteSelected"
              @select="setClient"
              v-validate="'minFields'"
              data-vv-name="clientId">
            </el-autocomplete>
            <small class="text-danger" v-show="showInvalidAutocompleteWarning('clientId')">
              {{ fieldNotValidError('Client') }}
            </small>
          </div>
          <div class="col-md-3"></div>
          <div class="col-md-3">
            <p-checkbox style="margin-top: 3px;"
                        name="location"
                        v-model="query.location">
              Location
            </p-checkbox>
          </div>
        </div>
        <div class="row" style="margin-top: 12px;">
          <div class="col-md-3">
            <label>Created Date
              <span class="text-danger" v-show="errors.has('querySearch')">
                {{ otherSearchFieldErrorText }}
              </span>
            </label>
            <el-date-picker type="daterange"
                            align="right"
                            unlink-panels
                            range-separator="-"
                            start-placeholder="Start date"
                            end-placeholder="End date"
                            v-model="query.createdAt"
                            :format="constants.DATE.FORMAT.DISPLAY"
                            :value-format="constants.DATE.FORMAT.VALUE"
                            :picker-options="datePickerConfig.dateRange"
                            placeholder="Created Date">
            </el-date-picker>
          </div>
          <div class="col-md-3">
            <label>Expiry Date
              <span class="text-danger" v-show="errors.has('querySearch')">
                {{ otherSearchFieldErrorText }}
              </span>
            </label>
            <el-date-picker type="daterange"
                            align="right"
                            unlink-panels
                            range-separator="-"
                            start-placeholder="Start date"
                            end-placeholder="End date"
                            v-model="query.expiry"
                            :format="constants.DATE.FORMAT.DISPLAY"
                            :value-format="constants.DATE.FORMAT.VALUE"
                            :picker-options="datePickerConfig.dateRange"
                            placeholder="Expiry Date">
            </el-date-picker>
          </div>
          <div class="col-md-3">
            <label>Date Updated
              <span class="text-danger" v-show="errors.has('querySearch')">
                {{ otherSearchFieldErrorText }}
              </span>
            </label>
            <el-date-picker type="daterange"
                            align="right"
                            unlink-panels
                            range-separator="-"
                            start-placeholder="Start date"
                            end-placeholder="End date"
                            v-model="query.updatedAt"
                            :format="constants.DATE.FORMAT.DISPLAY"
                            :value-format="constants.DATE.FORMAT.VALUE"
                            :picker-options="datePickerConfig.dateRange"
                            placeholder="Date Updated">
            </el-date-picker>
          </div>
        </div>
      </div><!-- card content -->
      <div class="card-footer row">
        <div class="col-md-12">
          <button type="button" @click="fetchSearchResults" class="btn btn-fill btn-info pull-right btn-search">
            Search
          </button>
          <button type="button" id="btn-clear" @click="clearSearchAndResults" class="btn btn-fill btn-info pull-right btn-clear">
            Clear
          </button>
        </div>
      </div>
      <div class="fix-search-container"></div>
    </div><!-- end card -->
    <div class="col-md-12 card">
      <div class="card-content row">
        <div class="col-sm-12" style="padding: 12px;">
          <div style="margin-bottom: 12px;">
            <strong>LEGEND: </strong><span class="warning-row">Orange rows indicate <u>expired</u> price rules.</span>
              <div class="pull-right">
                <a @click.prevent="downloadExcel" v-if="hasResults" href="#"><img src="static/img/icon-excel.jpg" title="Export to Excel" class="m-left-12"/></a>
                <a href="#" @click.prevent="handleShowPreferenceModal"><img style="margin-left: 12px;width: 26px; height: 26px;" src="static/img/gear-icon.png" title="Configure Columns Displayed" /></a>
              </div>
          </div>
          <div v-if="loading" style="text-align: center;">
            <img src="static/img/loading.gif"/>
          </div>
          <el-table :row-class-name="maybeExpiredPriceRule"
                    v-else
                    class="table-striped wrap-whole-words indexed-table-column-width"
                    :header-cell-class-name="headerSortClass"
                    :data="sortedTableData"
                    :key="tableKey"
                    border
                    style="width: 100%"
                    :max-height="tableHeight(400)">
            <el-table-column
              :width="this.constants.INDEX_COLUMN_WIDTHS.WIDE"
              align="center"
              label="#"
              type="index">
              <template v-slot="props">
                <span class="invisible">{{ props.row._id }}</span>
              </template>
            </el-table-column>
            <el-table-column type="expand" width="40">
              <template v-slot="props">
                <div style="max-width: 500px; padding: 12px 0 0 12px;">
                  <h6 v-if="props.row._source.notes">Notes</h6>
                  <ul v-if="props.row._source.notes">
                    <li> {{ props.row._source.notes }} </li>
                  </ul>

                  <h6>Audit Fields</h6>
                  <ul>
                    <li v-if="props.row._source.createdAt"><strong>Created on: </strong>{{ props.row._source.createdAt | formatDate }}</li>
                    <li v-if="props.row._source.createdBy"><strong>Created by: </strong>{{ getUserDisplayName(props.row._source.createdBy) }}</li>
                    <li v-if="props.row._source.updatedAt"><strong>Last Updated on: </strong>{{ props.row._source.updatedAt | formatDate }}</li>
                    <li v-if="props.row._source.updatedBy"><strong>Last Updated by: </strong>{{ getUserDisplayName(props.row._source.updatedBy) }}</li>
                    <li v-if="props.row._source.lastUsed"><strong>Last Used on: </strong>{{ props.row._source.lastUsed | formatDate }}</li>
                  </ul>
                </div>
              </template>
            </el-table-column>
            <el-table-column v-for="column in filteredTableColumns"
                             :key="column.label"
                             :min-width="column.minWidth"
                             :label="column.label">
              <template v-slot="props">
                <component :is="column.component" :data="getColumnData(column, props.row)" />
              </template>
            </el-table-column>
            <el-table-column
              v-if="canEdit"
              width="110"
              fixed="right"
              class="el-table__fixed"
              label="Actions">
              <template v-slot="props">
                <p-button type="success" size="sm" icon>
                  <i class="fa fa-edit" @click="editPriceRule(props.row)"></i>
                </p-button>
                <p-button type="danger" size="sm" icon @click="confirmDelete(props.row)">
                  <i class="fa fa-times"></i>
                </p-button>
              </template>
            </el-table-column>
          </el-table>
        </div>
        <div class="col-sm-6 pagination-info">
          <span class="category">Showing {{total}} entries</span>
        </div>
        <div class="col-sm-6">
          <p-button class="pull-right transparent" size="sm" icon @click="resultsNoValidate">
          </p-button>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import {mapGetters} from 'vuex'
import {
  assembleTableColumnSortProps,
  autocompleteReady,
  autocompleteTextButNoValue,
  autocompleteTextButNoValueMessage,
  filterAndSortTableColumns,
  findCommunities,
  formatCommentDate,
  getTableHeight,
  jobNamesForSelect,
  maxResultsWarning,
  notifyError,
  notifySuccess,
  notifyWarning,
  objectBy,
  sortNested,
  tableHeaderSortCssClass,
  verifyAutocompleteSelected
} from 'src/utilities/utils'
import Constants from 'src/utilities/constants'
import PButton from 'src/components/UIComponents/Button.vue'
import lodash from 'lodash'
import {minFields} from 'src/utilities/validation'
import Permissions from 'src/utilities/permissions'
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'
import {sync} from 'vuex-pathify'
import {NO_CONTENT, OK, UNAUTHORIZED} from 'http-status-codes'
import datePickerConfig from '@/components/UIComponents/DatePicker/datePickerConfig'
import tableColumns from '@/utilities/tableColumns'

export default {
  components: {
    PButton,
    vSelect
  },
  computed: {
    query: sync('priceRules@query'),
    ...mapGetters({
      user: 'security/user',
      tableData: 'priceRules/results',
      departments: 'departments/results',
      itemCodes: 'quickbooks/results',
      allDepartments: 'departments/kvpsForSelect',
      taskNamesForSelect: 'tasks/names/kvpsForSelect',
      autocompleteClients: 'companies/autocompleteResultsClient',
      projectTypes: 'types/project'
    }),
    sortedTableData() {
      return this.tableData.sort(sortNested(this.sortedProps))
    },
    filteredTableColumns() {
      return filterAndSortTableColumns(this.tableColumns, this.user, Constants.USER_PREFERENCE_TYPES.PRICE_RULES)
    },
    sortedProps() {
      return assembleTableColumnSortProps(this.filteredTableColumns)
    },
    jobNames() {
      const departments = this.$store.get('priceRules@query.departments')

      if (Array.isArray(departments) && departments.length > 0) {
        const departmentIds = []
        departments.forEach(departmentName => {
          const department = objectBy(this.allDepartments, 'label', departmentName)
          if (department) {
            departmentIds.push(department.value)
          }
        })

        return jobNamesForSelect(this, departmentIds)
      }

      return jobNamesForSelect(this)
    },
    canSearch() {
      return this.$can('read', Permissions.PRICE_RULES)
    },
    canEdit() {
      return this.$can('edit', Permissions.PRICE_RULES)
    },
    hasResults() {
      return this.tableData && this.tableData.length > 0
    },
    total() {
      return this.tableData.length
    }
  },
  data() {
    return {
      constants: Constants,
      datePickerConfig,
      selectedCommunity: { id: null, name: null },
      selectedClient: { id: null, name: null },
      toDelete: null,
      tableKey: 0,
      selectedTaskName: null,
      selectedJobName: null,
      mainSearchFieldErrorText: Constants.MESSAGES.AT_LEAST_ONE_FIELD_REQUIRED,
      otherSearchFieldErrorText: '*',
      loading: false,
      tableColumns: tableColumns.PRICE_RULE_SEARCH
    }
  },
  methods: {
    headerSortClass({columnIndex}) {
      return tableHeaderSortCssClass(this.filteredTableColumns, columnIndex, 2)
    },
    getColumnData(column, row) {
      return column.prop ? lodash.get(row, column.prop) : row
    },
    showInvalidAutocompleteWarning(refAndPathName) {
      // $ref has to match prop in store
      return autocompleteTextButNoValue(this, refAndPathName, this.$store.get(`priceRules/query@${refAndPathName}`))
    },
    fieldNotValidError(field) {
      return autocompleteTextButNoValueMessage(field)
    },
    downloadExcel() {
      this.$store.dispatch('priceRules/resultsToExcel')
    },
    getUserDisplayName(userId) {
      return this.$store.getters['users/displayName'](userId)
    },
    addPriceRule() {
      this.$eventHub.$emit('show-modal-1', 'Price Rule', null, '1300px')
    },
    editPriceRule(priceRuleResult) {
      this.$eventHub.$emit('show-modal-1', 'Price Rule', {id: priceRuleResult._id}, '1300px')
    },
    clearSearchAndResults() {
      this.$store.commit('priceRules/resetQuery')
      this.$store.commit('priceRules/resetResults')
      this.selectedTaskName = null
      this.selectedClient = { id: null, name: null }
      this.selectedJobName = null
      this.$validator.reset()
    },
    async findCommunities(text, cb) {
      await findCommunities(this, text, cb, 'priceRules/query@communityId')
    },
    verifyCommunityAutocompleteSelected() {
      verifyAutocompleteSelected(this, 'priceRules/query@communityId', 'community', this.selectedCommunity.name)
    },
    setCommunity(community) {
      if (community) {
        this.selectedCommunity.name = community.name
        this.$store.set('priceRules/query@communityId', community.id)
      }
    },
    noCommunitySelected() {
      this.selectedCommunity = { id: null, name: null }
    },
    maybeExpiredPriceRule({row}) {
      if (row._source.expiry && new Date(row._source.expiry) < new Date()) {
        return 'warning-row'
      }
    },
    updateSearchResults(priceRule) {
      this.$store.set('priceRules/updatePriceRule', priceRule)
    },
    async fetchSearchResults() {
      if (await this.$validator.validateAll()) {
        this.loading = true
        const response = await this.$store.dispatch('priceRules/fetchSearchResults')
        this.loading = false
        if (response.hits.total.value === 0) {
          this.$eventHub.$emit('show-modal-1', 'No Results Found', null, '500px')
        }
        if (response.hits.total.value > Constants.ELASTICSEARCH.MAX_SEARCH_RESULTS) {
          maxResultsWarning(this, Constants.ELASTICSEARCH.MAX_SEARCH_RESULTS)
        }
      }
    },
    resultsNoValidate() {
      this.$store.dispatch('priceRules/fetchSearchResults')
    },
    async delete() {
      const response = await this.$store.dispatch('delete', {
        endpoint: 'price-rules',
        id: this.toDelete
      })

      if (NO_CONTENT === response.status) {
        this.$store.commit('priceRules/delete', this.toDelete)
        notifySuccess(this, 'Price Rule Deleted Successfully')
      } else if (UNAUTHORIZED === response.status) {
        notifyWarning(this, 'You are not authorized to delete Price Rules')
      } else {
        notifyError(this, 'Error Deleting Price Rule')
      }
    },
    async confirmDelete(priceRuleResult) {
      const priceRule = priceRuleResult._source
      let message = ''
      let showButton = true
      this.toDelete = priceRuleResult._id

      const response = await this.$store.dispatch(
        'tasks/fetchByPriceRuleFieldValuesStatus', {
          name: Constants.TASKS.NAMES.INVOICE_SUBMITTAL,
          status: Constants.STATUSES.PENDING,
          key: 'priceRuleId',
          value: priceRuleResult._id
        })

      if (OK !== response.status) {
        notifyError(this, 'Failed to determine if price rule is in use. Delete cancelled.')
        return
      }

      if (response.data.length > 0) {
        showButton = false
        message = `Price rule with Short ID <strong>${priceRule.shortId}</strong> is in use by one or more Pending <strong>Invoice Submittal</strong> tasks and cannot be deleted.`
        this.showConfirmDelete(priceRule, message, showButton)
        return
      }

      let lastUsedMessage = null
      if (priceRule.lastUsed) {
        lastUsedMessage = `It was last used <strong>${formatCommentDate(priceRule.lastUsed)}</strong>`
      }

      if (!lastUsedMessage) {
        lastUsedMessage = 'This price rule has not been used before'
      }
      message = `Are you sure you want to delete price rule with Short ID <strong>${priceRule.shortId}</strong>? ${lastUsedMessage}. This will completely remove the price rule.`

      // happy path - price rule can be deleted
      showButton = true
      this.showConfirmDelete(priceRule, message, showButton)
    },
    showConfirmDelete(priceRule, message, showButton) {
      const payload = {
        message,
        entity: priceRule,
        delete: this.delete,
        showButton
      }
      this.$eventHub.$emit('show-modal-1', 'Confirm Delete', payload, '600px')
    },
    resetValidator() {
      this.$validator.reset()
    },
    async findClients(text, cb) {
      if (autocompleteReady(text)) {
        this.$store.set('priceRules/query@clientId', null)
        const payload = {
          type: Constants.COMPANY.TYPES.CLIENT,
          name: text
        }
        await this.$store.dispatch('companies/fetchByNameStartsWithAndType', payload)
        cb(this.autocompleteClients)
      }
    },
    tableHeight(buffer) {
      return getTableHeight(buffer)
    },
    handleShowPreferenceModal() {
      const payload = {
        title: 'Price Rules',
        preferenceType: Constants.USER_PREFERENCE_TYPES.PRICE_RULES
      }
      this.$eventHub.$emit('show-modal-1', 'Search and Sort Preferences', payload, '900px')
    },
    setClient(client) {
      if (client) {
        this.selectedClient.name = client.name
        this.selectedClient.id = client.id
        this.$store.set('priceRules/query@clientId', client.id)
        this.$store.commit('companies/autocompleteResultsClient', [])
        this.$validator.reset()
      }
    },
    verifyClientAutocompleteSelected() {
      verifyAutocompleteSelected(this, 'priceRules/query@clientId', 'client', this.selectedClient.name)
    },
    setTaskName(taskName) {
      this.$store.set('priceRules/query@taskName', null)
      if (taskName) {
        this.$store.set('priceRules/query@taskName', taskName.label)
      }
    },
    setJobName(jobName) {
      this.$store.set('priceRules/query@jobName', null)
      if (jobName) {
        this.$store.set('priceRules/query@jobName', jobName.label)
      }
    }
  },
  async beforeCreate() {
    await Promise.all([
      this.$store.cache.dispatch('tasks/names/fetchAll'),
      this.$store.cache.dispatch('quickbooks/fetchAll', true),
      this.$store.cache.dispatch('types/fetchByEntityType', 'Project'),
      this.$store.cache.dispatch('camunda/fetchAllPtracDeployments')
    ])
  },
  mounted() {
    this.$store.commit('priceRules/resetQuery')
    this.$eventHub.$on('priceRuleSaved', this.updateSearchResults)
    this.$eventHub.$on('communitySaved', this.setCommunity)
    this.$eventHub.$on('noCommunitySelected', this.noCommunitySelected)
    minFields('priceRules')
    this.$validator.reset()
  },
  beforeDestroy() {
    this.$eventHub.$off('priceRuleSaved')
    this.$eventHub.$off('communitySaved')
    this.$eventHub.$off('noCommunitySelected')
  },
  watch: {
    filteredTableColumns: function() {
      this.tableKey++
    },
    'selectedCommunity.name': {
      handler: function (selectedCommunityName) {
        if (!selectedCommunityName) {
          this.$store.set('priceRules/query@communityId', null)
          this.selectedCommunity = { id: null, name: null }
        }
      }
    }
  }
}
</script>
<style scoped>
  #btn-clear {
    margin: 0 12px;
  }
</style>
